/**
 * Data transformation utilities for the new property intelligence layout
 * Converts existing HomeIntelligenceData to the new full-screen layout format
 */

import type {
  HomeIntelligenceData,
  PropertyIntelligenceScore,
  QuickInsight,
  ImportanceLevel
} from '../../shared/intelligence/types.js';
import type {
  IntelligenceDriver,
  IntelligenceInsight,
  IntelligenceRisk,
  TransformedIntelligenceData
} from '../types/intelligence.js';

export type { IntelligenceDriver, IntelligenceInsight, IntelligenceRisk, TransformedIntelligenceData };

/**
 * Convert importance level to display string
 */
function importanceToLevel(importance: ImportanceLevel): string {
  const levelMap: Record<ImportanceLevel, string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    critical: 'Critical'
  };
  return levelMap[importance];
}

/**
 * Generate score-based label for overall intelligence score
 */
function getOverallLabel(score: number): string {
  if (score >= 85) return 'Outstanding investment opportunity';
  if (score >= 70) return 'Strong investment opportunity';
  if (score >= 55) return 'Good investment potential';
  if (score >= 40) return 'Moderate investment potential';
  return 'Limited investment potential';
}

/**
 * Generate percentile text based on score
 */
function getPercentileText(score: number): string {
  if (score >= 90) return 'Top ~5% in local market';
  if (score >= 80) return 'Top ~15% in local market';
  if (score >= 70) return 'Top ~25% in local market';
  if (score >= 60) return 'Top ~40% in local market';
  if (score >= 50) return 'Above average in local market';
  return 'Below average in local market';
}

/**
 * Convert property intelligence scores to driver format
 */
function convertScoresToDrivers(scores: PropertyIntelligenceScore): IntelligenceDriver[] {
  return [
    {
      key: 'momentum',
      name: 'Market Momentum',
      score: scores.marketMomentum,
      why: scores.marketMomentum >= 70
        ? 'Strong market activity with good momentum indicators.'
        : 'Market momentum is moderate; trends suggest stability.',
      action: scores.marketMomentum >= 70
        ? 'Act quickly - prepare competitive offer strategy.'
        : 'Monitor market trends and prepare for opportunities.'
    },
    {
      key: 'market',
      name: 'Market Position',
      score: scores.marketPosition,
      why: scores.marketPosition >= 70
        ? 'Well-positioned relative to comparable properties.'
        : 'Market positioning has room for improvement.',
      action: scores.marketPosition >= 70
        ? 'Leverage strong market position in negotiations.'
        : 'Research comparables to strengthen market position.'
    },
    {
      key: 'condition',
      name: 'Property Condition',
      score: scores.propertyCondition,
      why: scores.propertyCondition >= 70
        ? 'Property shows good condition with minimal red flags.'
        : 'Some condition concerns identified that need attention.',
      action: scores.propertyCondition >= 70
        ? 'Request maintenance history and recent inspection reports.'
        : 'Budget for condition improvements and detailed inspection.'
    },
    {
      key: 'risk',
      name: 'Risk Factors',
      score: scores.riskFactors,
      why: scores.riskFactors >= 70
        ? 'Low to moderate risk profile identified.'
        : 'Several risk factors require careful consideration.',
      action: scores.riskFactors >= 70
        ? 'Standard due diligence procedures recommended.'
        : 'Enhanced due diligence with risk mitigation planning.'
    },
    {
      key: 'potential',
      name: 'Investment Potential',
      score: scores.investmentPotential,
      why: scores.investmentPotential >= 70
        ? 'Strong potential for appreciation and returns.'
        : 'Investment potential depends on market conditions.',
      action: scores.investmentPotential >= 70
        ? 'Model value-add opportunities and rental potential.'
        : 'Analyze local market trends and improvement ROI.'
    }
  ];
}

/**
 * Convert QuickInsights to new format
 */
function convertInsights(insights: QuickInsight[]): IntelligenceInsight[] {
  return insights.slice(0, 4).map(insight => ({
    title: insight.title,
    level: importanceToLevel(insight.importance),
    text: insight.description,
    type: insight.type
  }));
}

/**
 * Generate sample risk flags based on score and data
 */
function generateRiskFlags(scores: PropertyIntelligenceScore): IntelligenceRisk[] {
  const risks: IntelligenceRisk[] = [];

  // Risk based on overall risk factor score
  if (scores.riskFactors < 60) {
    risks.push({ text: 'Multiple risk factors identified', severity: 'high' });
  } else if (scores.riskFactors < 80) {
    risks.push({ text: 'Moderate risk considerations', severity: 'medium' });
  }

  // Market-based risks
  if (scores.marketMomentum < 50) {
    risks.push({ text: 'Slow market conditions', severity: 'medium' });
  }

  // Property condition risks
  if (scores.propertyCondition < 60) {
    risks.push({ text: 'Property condition concerns', severity: 'high' });
  }

  // Add some static examples if we don't have many risks
  if (risks.length < 2) {
    risks.push(
      { text: 'Verify insurance premium trends', severity: 'low' },
      { text: 'Review local zoning regulations', severity: 'low' }
    );
  }

  return risks.slice(0, 5); // Limit to 5 risks
}

/**
 * Main transformation function
 */
export function transformIntelligenceData(
  data: HomeIntelligenceData,
  address: string
): TransformedIntelligenceData {
  return {
    address,
    overall: Math.round(data.propertyScore.overall),
    overallLabel: getOverallLabel(data.propertyScore.overall),
    percentileText: getPercentileText(data.propertyScore.overall),
    subscores: convertScoresToDrivers(data.propertyScore),
    insights: convertInsights(data.quickInsights),
    risks: generateRiskFlags(data.propertyScore)
  };
}